const API_URL = process.env.REACT_APP_API_URL;
const AGENTS_URL = `${API_URL}/api/agents`;

// Create a new agent
export const createAgent = async agentData => {
    try {
        const response = await fetch(AGENTS_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(agentData),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to create agent');
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// Get agent by slug
export const getAgentBySlug = async slug => {
    try {
        const response = await fetch(`${AGENTS_URL}/${slug}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch agent');
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// Get agent details for management (owner only)
// Not used for now, will be used for agent management (settings, etc.)
export const getAgentDetailsForOwner = async slug => {
    try {
        const response = await fetch(`${AGENTS_URL}/${slug}/details`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch agent details');
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// Get agent by chat id
export const getAgentByChatId = async agentChatId => {
    try {
        const response = await fetch(`${AGENTS_URL}/chatId/${agentChatId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch agent chat');
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// Get all agents for the authenticated user
export const getUserAgents = async () => {
    try {
        const response = await fetch(AGENTS_URL, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch agents');
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// Get agents the user interacted with or has access to (excluding user's own)
export const getInteractedAgents = async () => {
    try {
        const response = await fetch(`${AGENTS_URL}/interacted`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch interacted agents');
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// Get chats for a specific agent with pagination
export const getAgentChats = async (slug, page = 1, limit = 50, accessToken = null) => {
    try {
        const url = new URL(`${AGENTS_URL}/${slug}/chats`);
        url.searchParams.append('page', page.toString());
        url.searchParams.append('limit', limit.toString());

        if (accessToken) {
            url.searchParams.append('token', accessToken);
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch agent chats');
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// Get user's chats with a specific agent (for sidebar)
export const getUserAgentChats = async (slug, page = 1, limit = 50, accessToken = null) => {
    try {
        const url = new URL(`${AGENTS_URL}/${slug}/user-chats`);
        url.searchParams.append('page', page.toString());
        url.searchParams.append('limit', limit.toString());

        if (accessToken) {
            url.searchParams.append('token', accessToken);
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to fetch user agent chats');
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// Update agent (owner only)
export const updateAgent = async (slug, updateData) => {
    try {
        const response = await fetch(`${AGENTS_URL}/${slug}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(updateData),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to update agent');
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// Delete agent (owner only)
export const deleteAgent = async slug => {
    try {
        const response = await fetch(`${AGENTS_URL}/${slug}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            throw new Error(data.error || 'Failed to delete agent');
        }

        return data;
    } catch (error) {
        throw error;
    }
};

// Check slug availability
export const checkSlugAvailability = async slug => {
    try {
        const response = await fetch(`${AGENTS_URL}/check-slug`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ slug }),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to check slug availability');
        }

        return data;
    } catch (error) {
        throw error;
    }
};
